import { ArrowRight, CalendarCheck } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { ParentWorkItem } from '../../types/parent';
import { daysLeftLabel, formatShortDay } from '../../utils';

export interface UpcomingDeadlinesProps {
  /** Every task for the child, unfiltered — see the note in the card below. */
  items: ParentWorkItem[];
  today: string;
  /** Opens one of them in full. */
  onOpen: (uuid: string) => void;
  /** Drops the filters and puts the whole list back in date order. */
  onViewAll: () => void;
}

/** How many the rail shows before "view all" earns its place. */
const SHOWN = 3;

/**
 * What is coming, soonest first.
 *
 * Deliberately not a second copy of the list: it shows only what is *not*
 * finished and only what is still ahead, which is the one question the table
 * cannot answer at a glance while it is sorted, filtered or on page three.
 *
 * Anything already handed in is left out even if its date is still ahead —
 * a deadline that has been met is not a deadline any more.
 */
export function UpcomingDeadlines({ items, today, onOpen, onViewAll }: UpcomingDeadlinesProps) {
  const upcoming = items
    .filter((item) => !item.submittedAt && item.dueDate >= today)
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate));

  const shown = upcoming.slice(0, SHOWN);

  return (
    <section
      className="rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden shadow-xs flex flex-col"
      aria-label="Upcoming deadlines"
    >
      <header className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 px-4 py-3">
        <CalendarCheck className="h-4 w-4 text-indigo-600 dark:text-indigo-400" aria-hidden />
        <h2 className="text-sm sm:text-base font-bold tracking-tight text-slate-800 dark:text-white">
          Upcoming Deadlines
        </h2>
      </header>

      {shown.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm font-medium text-slate-500 dark:text-slate-400">
          Nothing due from today onwards.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-slate-100 dark:divide-slate-800">
          {shown.map((item) => {
            const daysLeft = Math.ceil(
              (new Date(`${item.dueDate}T00:00:00`).getTime() - new Date(`${today}T00:00:00`).getTime()) /
                (1000 * 60 * 60 * 24),
            );

            return (
              <li key={item.uuid}>
                <button
                  type="button"
                  onClick={() => onOpen(item.uuid)}
                  className={cn(
                    'flex w-full items-start justify-between gap-3 px-4 py-3 text-left transition-colors cursor-pointer',
                    'hover:bg-slate-50 dark:hover:bg-slate-800/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                  )}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-xs font-bold text-blue-600 dark:text-blue-400">
                      {item.subject}
                    </span>
                    <span className="mt-0.5 block truncate text-sm font-bold text-slate-900 dark:text-white">
                      {item.title}
                    </span>
                    <span className="mt-1 block truncate text-xs font-semibold text-slate-500 dark:text-slate-400">
                      {formatShortDay(item.dueDate)}
                    </span>
                  </span>

                  <span
                    className={cn(
                      'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold shrink-0',
                      daysLeft <= 3
                        ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20'
                        : 'bg-sky-500/10 text-sky-700 dark:text-sky-400 border border-sky-500/20',
                    )}
                  >
                    {daysLeftLabel(item.dueDate, today)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {upcoming.length > SHOWN && (
        <div className="border-t border-slate-100 dark:border-slate-800 px-4 py-2.5 text-center">
          <button
            type="button"
            onClick={onViewAll}
            className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 cursor-pointer"
          >
            <span>View all {upcoming.length}</span>
            <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      )}
    </section>
  );
}
